<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useRoute, useRouter } from 'vue-router';
import SettingsLayout from 'dashboard/routes/dashboard/settings/SettingsLayout.vue';
import BaseSettingsHeader from 'dashboard/routes/dashboard/settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import { fetchTiendaNubeAllOrders } from '../../api/tiendaNube';

const store = useStore();
const route = useRoute();
const router = useRouter();

const isLoading = ref(true);
const records = ref([]);
const error = ref('');

const accountId = computed(() => Number(route.params.accountId));

const loadRecords = async () => {
  try {
    isLoading.value = true;
    error.value = '';
    const { data } = await fetchTiendaNubeAllOrders(accountId.value);
    records.value = data.orders || [];
  } catch {
    error.value = 'TIENDA_NUBE_PAGES.ORDERS.ERROR';
  } finally {
    isLoading.value = false;
  }
};

const openContact = contactId => {
  if (!contactId) return;
  router.push({
    name: 'contacts_edit',
    params: { accountId: accountId.value, contactId },
  });
};

onMounted(() => {
  loadRecords();
  store.dispatch('integrations/get', 'tienda_nube');
});
</script>

<template>
  <SettingsLayout :is-loading="isLoading">
    <template #header>
      <BaseSettingsHeader
        :title="$t('TIENDA_NUBE_PAGES.ORDERS.TITLE')"
        :description="$t('TIENDA_NUBE_PAGES.ORDERS.DESCRIPTION')"
      />
    </template>
    <template #body>
      <div class="flex flex-col gap-4">
        <div
          v-if="error"
          class="rounded-md border border-n-ruby-5 bg-n-ruby-2 p-4 text-sm text-n-ruby-12"
        >
          {{ $t(error) }}
        </div>
        <div
          v-else-if="!records.length"
          class="rounded-md border border-dashed border-n-weak p-6 text-sm text-n-slate-11"
        >
          {{ $t('TIENDA_NUBE_PAGES.ORDERS.EMPTY') }}
        </div>
        <div v-else class="grid gap-4">
          <div
            v-for="order in records"
            :key="order.id"
            class="rounded-md border border-n-weak bg-n-alpha-3 p-4"
          >
            <div class="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
              <div class="space-y-1 min-w-0">
                <p class="mb-0 text-base font-semibold text-n-slate-12">
                  {{ $t('TIENDA_NUBE_PAGES.ORDERS.ORDER_NUMBER', { number: order.number || order.id }) }}
                </p>
                <p class="mb-0 text-sm text-n-slate-11">
                  {{ order.customer_name || order.customer_email || order.customer_phone || '—' }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ order.customer_email || '—' }} · {{ order.customer_phone || '—' }}
                </p>
                <p class="mb-0 text-xs text-n-slate-10">
                  {{ order.status || '—' }} · {{ order.payment_status || '—' }} · {{ order.total || '—' }} {{ order.currency || '' }}
                </p>
              </div>
              <div class="flex flex-wrap gap-2">
                <Button
                  v-if="order.admin_url"
                  variant="outline"
                  color="slate"
                  :label="$t('TIENDA_NUBE_PAGES.OPEN_ORDER')"
                  @click="window.open(order.admin_url, '_blank', 'noopener,noreferrer')"
                />
                <Button
                  :disabled="!order.contact_id"
                  :label="$t('TIENDA_NUBE_PAGES.OPEN_CONTACT_TO_MESSAGE')"
                  @click="openContact(order.contact_id)"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </SettingsLayout>
</template>
